import { CHANNELS } from '../../../src/ipc/channels';
import { ApiError, apiRequest } from '../../../src/services/api';
import { requireUserId } from '../../../src/services/supabase/auth';
import { markArchived, type StoredS3Keys } from '../../../src/services/supabase/meetingsRepo';
import type { GeneratedSummary, TranscriptSegmentDTO } from '../../../src/shared/types';
import { emit } from '../bus';
import type { StoredFile } from '../idb';
import { isRecordingPath, readRecording, removeRecording, safeRecordingPath } from './recordings';
import { isS3Configured, meetingKeys, putJson, transfer } from './s3';
import { contentSealer } from '../../../src/services/crypto/sealContent';

/**
 * `meetingArchive.ts`'nin web karşılığı. Masaüstünde ses ffmpeg ile mp3'e
 * çevrilip yükleniyor; tarayıcıda ffmpeg yok. Ses olduğu gibi (webm/opus ya da
 * yüklenen dosyanın biçimi) depoya gidiyor, sunucu mp3'e çevirip toplantının
 * klasörüne koyuyor (`/api/v1/meetings/:id/audio`). Sıra masaüstündekiyle
 * aynı: önce yükle, sonra tarayıcıdaki kopyayı sil — yarıda kalırsa bir
 * sonraki turda (botSync > retryPendingArchives) yeniden denenir.
 */

export function notifyMeetingsChanged(): void {
  emit(CHANNELS.meetingsChanged);
}

export interface ArchiveInput {
  meetingId: string;
  userId: string;
  audioPath?: string;
  transcript: TranscriptSegmentDTO[];
  summary: GeneratedSummary;
}

/** Sunucunun kabul ettiği ses türleri (web/lib/storage/s3.ts > UPLOAD_TYPES ile aynı). */
const UPLOAD_TYPES = new Set([
  'audio/webm',
  'audio/ogg',
  'audio/mpeg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/flac',
  'audio/aac',
  'video/webm',
  'video/mp4',
]);

/** Tarayıcının verdiği tür (`audio/webm;codecs=opus`) sunucunun listesine indirgenir. */
export function uploadContentType(blob: Blob, fileName = ''): string {
  const base = blob.type.split(';')[0].trim().toLowerCase();
  if (UPLOAD_TYPES.has(base)) return base;
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  const byExt: Record<string, string> = {
    webm: 'audio/webm',
    weba: 'audio/webm',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    opus: 'audio/ogg',
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    mp4: 'video/mp4',
    aac: 'audio/aac',
    wav: 'audio/wav',
    flac: 'audio/flac',
  };
  return byExt[ext] ?? 'audio/webm';
}

interface SignedUpload {
  uploadId: string;
  url: string;
  contentType: string;
}

/**
 * Sesi sunucunun geçici yükleme alanına koyar; yükleme kimliğini döner.
 * `path` imzalı adresi veren uç (toplantı sesi ya da dosya dökümü).
 */
export async function uploadAudioBlob(
  blob: Blob,
  fileName: string,
  path: string,
  extraBody: Record<string, unknown> = {}
): Promise<string> {
  const signed = await apiRequest<SignedUpload>('POST', path, {
    body: { ...extraBody, sizeBytes: blob.size, contentType: uploadContentType(blob, fileName) },
  });
  await transfer(signed.url, { method: 'PUT', headers: { 'Content-Type': signed.contentType }, body: blob });
  return signed.uploadId;
}

async function finalizeAudio(meetingId: string, uploadId: string): Promise<string> {
  const { audioKey } = await apiRequest<{ audioKey: string }>('POST', `/api/v1/meetings/${meetingId}/audio`, {
    body: { step: 'finalize', uploadId },
    // Uzun kayıtların sunucuda mp3'e çevrilmesi zaman alabiliyor.
    timeoutMs: 10 * 60_000,
  });
  return audioKey;
}

async function uploadMeetingAudio(meetingId: string, file: StoredFile): Promise<string> {
  // Dosya yüklemede sunucu dökümden sonra mp3 kopyasını tutuyor — yeniden yükleme gerekmez.
  if (file.serverUploadId) {
    try {
      return await finalizeAudio(meetingId, file.serverUploadId);
    } catch (err) {
      // Kopya bakım işine takılmış olabilir (2 sa); o zaman dosyanın kendisini yükle.
      if (!(err instanceof ApiError && err.status === 404)) throw err;
    }
  }
  const uploadId = await uploadAudioBlob(file.blob, file.name, `/api/v1/meetings/${meetingId}/audio`, { step: 'sign' });
  return finalizeAudio(meetingId, uploadId);
}

export async function uploadMeetingFiles(input: ArchiveInput): Promise<StoredS3Keys> {
  // İçerik şifreleme açıksa `.tn1` anahtarlar; s3 shim'i içeriği yüklemeden önce şifreler.
  // Ses sunucuda mp3'e çevrilip orada şifreleniyor; dönen anahtar onu gösterir.
  const sealer = await contentSealer(input.userId, input.meetingId);
  const keys = meetingKeys(input.userId, input.meetingId, sealer.enabled);
  let audioKey: string | undefined;
  if (input.audioPath && isRecordingPath(input.audioPath)) {
    audioKey = await uploadMeetingAudio(input.meetingId, await readRecording(input.audioPath));
  }
  await Promise.all([putJson(keys.transcriptKey, input.transcript), putJson(keys.summaryKey, input.summary)]);
  return { audioKey, transcriptKey: keys.transcriptKey, summaryKey: keys.summaryKey };
}

const inFlight = new Set<string>();

export async function archiveRecordedMeeting(
  input: Omit<ArchiveInput, 'userId'> & { audioPath: string }
): Promise<boolean> {
  if (!isS3Configured()) return false;
  if (inFlight.has(input.meetingId)) return false;
  inFlight.add(input.meetingId);
  try {
    const audioPath = await safeRecordingPath(input.audioPath);
    if (!audioPath) return false;
    const userId = await requireUserId();
    const keys = await uploadMeetingFiles({ ...input, audioPath, userId });
    await markArchived(input.meetingId, keys);
    await removeRecording(audioPath).catch(() => {});
    notifyMeetingsChanged();
    return true;
  } catch (err) {
    console.error('[archive] arşivleme başarısız, sonra yeniden denenecek:', input.meetingId, err);
    return false;
  } finally {
    inFlight.delete(input.meetingId);
  }
}
